import { useEffect, useState } from 'react'
import { price as fmtPrice } from '../format'
import { fetchPnl, type PnlDay, type PnlReport, type PnlRow, type PnlTotals } from '../pnl'
import { useIsMobile } from '../useMediaQuery'
import { DateRangePicker } from './DateRangePicker'
import { MonthPicker, ayIso } from './MonthPicker'
import { PnlBarChart, PnlCumulativeChart } from './PnlCharts'
import { PnlShareCard } from './PnlShareCard'

/**
 * PnL Takip — kalıcı kayıttan (fills + hourly_prices) hesaplanan kâr/zarar.
 *
 * İki görünüm (kullanıcı isteği 2026-08-01):
 *  • GÜNLÜK — bir gün, satırlar o günün teslim saatleri (kuruluş görünümü);
 *    tüm kırılım (GİP bacakları, dengesizlik, finansal etki) burada durur.
 *  • AYLIK  — bir ay, satırlar o ayın günleri. Solda YARIM ekran genişliğinde
 *    sade bir tablo (yalnız sonuç sütunları), sağda üst üste iki grafik:
 *    günlük PnL sütunları + kümülatif PnL çizgisi (kullanıcı isteği
 *    2026-08-02: "detaya gerek yok, sonuç ve seyir görünsün").
 * İkisi de AYNI uçtan (`/api/pnl?since&until`) beslenir: sunucu zaten gün gün
 * `total` üretiyor, aylık görünüm o toplamları satır yapar — ikinci bir hesap
 * yolu açılmadı (kart/tablo/grafik tutarsızlığı imkânsız kalsın).
 *
 * Üst bantta görünüm anahtarı, ona göre aralık takvimi ya da ay seçici, boş
 * satır anahtarı ve (yalnız günlükte) TAM ORTADA gün sayfalayıcı; kartlar her
 * iki görünümde de SEÇİLİ ARALIĞIN tamamını özetler.
 *
 * Hesap tamamen sunucuda (src/shadow/store/pnl.py) — burada yalnız gösterim.
 * SMF açıklanmamışsa sunucu SMF=PTF varsayar ve satırı işaretler; o hücre
 * silik + italik görünür (geçici olduğu anlaşılsın, kullanıcı isteği).
 */

const mwhFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})
const tlFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const pctFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const DASH = '—'
const fmtMwh = (v: number | null | undefined) => (v == null ? DASH : mwhFmt.format(v))
const fmtTl = (v: number | null | undefined) => (v == null ? DASH : tlFmt.format(v))
const fmtPct = (v: number | null | undefined) => (v == null ? DASH : `%${pctFmt.format(v)}`)

/** Pazar = 0 (Date.getDay sırası). */
const HAFTA_ADI = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt']

/** YYYY-MM-DD → GG.AA.YYYY (kullanıcının istediği biçim). */
function tarih(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}

/** YYYY-MM-DD → "Cmt". Yerel saate düşmesin diye parça parça kuruluyor. */
function haftaGunu(iso: string): string {
  const yil = Number(iso.slice(0, 4))
  const ay = Number(iso.slice(5, 7))
  const gun = Number(iso.slice(8, 10))
  return HAFTA_ADI[new Date(yil, ay - 1, gun).getDay()] ?? ''
}

/** Hafta sonu satırı hafifçe ayrışsın — tahtanın ritmi hafta içi/sonu. */
function haftaSonu(iso: string): boolean {
  const g = haftaGunu(iso)
  return g === 'Cmt' || g === 'Paz'
}

/** İşarete göre kâr/zarar rengi; sıfır ve boş nötr kalır. */
function ton(v: number | null | undefined): string {
  if (v == null || v === 0) return ''
  return v > 0 ? ' pnl-poz' : ' pnl-neg'
}

function isoGun(d: Date): string {
  const ay = String(d.getMonth() + 1).padStart(2, '0')
  const gun = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${ay}-${gun}`
}

/** YYYY-MM → ayın ilk ve son günü (ISO). */
function ayAraligi(ay: string): [string, string] {
  const yil = parseInt(ay.slice(0, 4), 10)
  const ayIdx = parseInt(ay.slice(5, 7), 10) - 1
  const sonGun = new Date(yil, ayIdx + 1, 0).getDate()
  return [`${ay}-01`, `${ay}-${String(sonGun).padStart(2, '0')}`]
}

/** Günün hiç dolumu var mı? Yoksa satır "boş" — varsayılan gizli. */
function doluGun(g: PnlDay): boolean {
  return g.total.buy.mwh > 0 || g.total.sell.mwh > 0
}

export function PnLPage() {
  // Varsayılan aralık: son 7 gün (bugün dahil) — kartlar dolu açılsın.
  const bugun = new Date()
  const hafta = new Date(bugun)
  hafta.setDate(bugun.getDate() - 6)
  const [since, setSince] = useState(isoGun(hafta))
  const [until, setUntil] = useState(isoGun(bugun))

  /** Görünüm: gün gün saatler mi, ay içinde günler mi. */
  const [gorunum, setGorunum] = useState<'gun' | 'ay'>('gun')
  const [rapor, setRapor] = useState<PnlReport | null>(null)
  const [gunIdx, setGunIdx] = useState(0)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)
  /** Paylaşım kartı açık olan satır — satırın solundaki ikonla açılır. */
  const [kartSatiri, setKartSatiri] = useState<PnlRow | null>(null)
  /** Dolumu olmayan saat/gün satırları görünsün mü? Varsayılan gizli. */
  const [bosGoster, setBosGoster] = useState(false)
  /** Aylık görünümde vurgulanan gün (dizin) — tablo ve iki grafik ORTAK
   *  kullanır: satırın üstüne gelmek grafikteki günü, grafikte gezinmek
   *  tablodaki satırı işaretler. */
  const [vurgu, setVurgu] = useState<number | null>(null)

  useEffect(() => {
    if (!since || !until) return
    if (until < since) {
      setHata('Bitiş, başlangıçtan önce olamaz')
      return
    }
    let iptal = false
    setYukleniyor(true)
    setHata(null)
    fetchPnl(since, until)
      .then((r) => {
        if (iptal) return
        setRapor(r)
        setGunIdx(r.days.length - 1) // en yeni gün açılsın
      })
      .catch((e) => !iptal && setHata(e instanceof Error ? e.message : 'PnL alınamadı'))
      .finally(() => !iptal && setYukleniyor(false))
    return () => {
      iptal = true
    }
  }, [since, until])

  const gunler = rapor?.days ?? []
  const gun = gunler[Math.min(gunIdx, Math.max(0, gunler.length - 1))]
  // Boş saat = dolumu olmayan satır (PnL de üretmez). Varsayılan gizli;
  // anahtar 24 satırın tamamını geri getirir.
  const satirlar = gun
    ? bosGoster
      ? gun.rows
      : gun.rows.filter((r) => r.buy.mwh > 0 || r.sell.mwh > 0)
    : []
  // Aylık satırlar: gün nesnesi + ASIL sırası (tıklanınca günlüğe o gün açılsın).
  const ayGunleri = gunler
    .map((g, i) => ({ g, i }))
    .filter(({ g }) => bosGoster || doluGun(g))

  // Aylık görünümde seçili ay = aralığın başladığı ay; ileri sınır içinde
  // bulunulan ayın bir sonrası (yarının kontratı öbür aya taşabiliyor).
  const seciliAy = since.slice(0, 7)
  const maxAy = ayIso(bugun.getFullYear(), bugun.getMonth() + 1)

  const ayaGec = () => {
    const [b, s] = ayAraligi((gun?.date ?? until).slice(0, 7))
    setSince(b)
    setUntil(s)
    setGorunum('ay')
  }
  /** Günlüğe dön; `idx` verilmezse kayıt taşıyan SON güne düş (boşa değil). */
  const gunlugeGec = (idx?: number) => {
    setVurgu(null)                 // aylığa dönünce bayat vurgu asılı kalmasın
    if (idx != null) setGunIdx(idx)
    else {
      const son = gunler.map(doluGun).lastIndexOf(true)
      setGunIdx(son >= 0 ? son : Math.max(0, gunler.length - 1))
    }
    setGorunum('gun')
  }
  const ayDegis = (a: string) => {
    const [b, s] = ayAraligi(a)
    setSince(b)
    setUntil(s)
  }

  return (
    <main className="ledger pnl-page">
      <div className="ledger-panel">
        <div className="ledger-filters pnl-filterbar">
          <div className="pnl-seg" role="group" aria-label="Görünüm">
            <button
              type="button"
              className={'pnl-seg-btn' + (gorunum === 'gun' ? ' aktif' : '')}
              aria-pressed={gorunum === 'gun'}
              onClick={() => gunlugeGec()}
              title="Bir günün saatleri"
            >
              Günlük
            </button>
            <button
              type="button"
              className={'pnl-seg-btn' + (gorunum === 'ay' ? ' aktif' : '')}
              aria-pressed={gorunum === 'ay'}
              onClick={ayaGec}
              title="Bir ayın günleri"
            >
              Aylık
            </button>
          </div>

          {gorunum === 'gun' ? (
            <DateRangePicker
              since={since}
              until={until}
              onChange={(s, u) => {
                setSince(s)
                setUntil(u)
              }}
            />
          ) : (
            <MonthPicker ay={seciliAy} maxAy={maxAy} onChange={ayDegis} />
          )}

          <label
            className="switch"
            title={
              gorunum === 'ay'
                ? 'İşlem olmayan günleri göster/gizle'
                : 'Dolumu olmayan saat satırlarını göster/gizle'
            }
          >
            <input
              type="checkbox"
              checked={bosGoster}
              onChange={(e) => setBosGoster(e.target.checked)}
            />
            <span className="switch-track" aria-hidden="true">
              <span className="switch-thumb" />
            </span>
            <span className="switch-text">
              {gorunum === 'ay' ? 'Boş günler' : 'Boş saatler'}
            </span>
          </label>

          {gorunum === 'gun' && (
            <div className="pnl-pager-top">
              <button
                type="button"
                className="pager-btn"
                onClick={() => setGunIdx((i) => Math.max(0, i - 1))}
                disabled={gunIdx <= 0}
                aria-label="Önceki gün"
              >
                ‹
              </button>
              <span className="pager-label pnl-pager-label">{gun ? tarih(gun.date) : DASH}</span>
              <button
                type="button"
                className="pager-btn"
                onClick={() => setGunIdx((i) => Math.min(gunler.length - 1, i + 1))}
                disabled={gunIdx >= gunler.length - 1}
                aria-label="Sonraki gün"
              >
                ›
              </button>
            </div>
          )}
          <span className="ledger-count">{yukleniyor ? 'hesaplanıyor…' : ''}</span>
        </div>

        {hata && <div className="order-error">{hata}</div>}

        {rapor && <Kartlar ozet={rapor.summary} aylik={gorunum === 'ay'} />}

        {gorunum === 'ay' ? (
          <div className="pnl-ay-body">
            {/* Sol yarı: yalnız sonuç sütunları, dikeyde kaydırılır. Başlık
                bandı grafik panelleriyle aynı — üç kutu kardeş görünsün. */}
            <div className="panel pnl-ay-sol">
              <div className="panel-title">
                Günler
                <span className="panel-meta">
                  {ayGunleri.length} gün{bosGoster ? '' : ' · işlemli'}
                </span>
              </div>
              <div className="pnl-ay-tablo">
                {rapor && ayGunleri.length > 0 ? (
                  <AylikTable
                    gunler={ayGunleri}
                    total={rapor.summary}
                    onGun={gunlugeGec}
                    vurgu={vurgu}
                    onVurgu={setVurgu}
                  />
                ) : (
                  <div className="shadow-empty">
                    {yukleniyor
                      ? ''
                      : rapor
                        ? 'Bu ayda işlem yok — tüm günleri görmek için "Boş günler" anahtarını aç.'
                        : 'Bu aralıkta kayıt yok.'}
                  </div>
                )}
              </div>
            </div>
            {/* Sağ yarı: üst üste iki grafik, ikisi de AYIN TAMAMINI çizer
                (tablodaki "boş günler" süzgeci grafiği etkilemez — takvim
                ekseninde günün yerinin boş kalması bilgidir). */}
            <div className="pnl-ay-sag">
              <PnlBarChart gunler={gunler} vurgu={vurgu} onVurgu={setVurgu} onGun={gunlugeGec} />
              <PnlCumulativeChart gunler={gunler} vurgu={vurgu} onVurgu={setVurgu} onGun={gunlugeGec} />
            </div>
          </div>
        ) : (
        <div className="ledger-body">
          {gun && satirlar.length > 0 ? (
            <PnlTable rows={satirlar} total={gun.total} onKart={setKartSatiri} />
          ) : gun ? (
            <div className="shadow-empty">
              Bu günde işlem yok — tüm saatleri görmek için "Boş saatler" anahtarını aç.
            </div>
          ) : (
            <div className="shadow-empty">{yukleniyor ? '' : 'Bu aralıkta kayıt yok.'}</div>
          )}
        </div>
        )}

        {kartSatiri && <PnlShareCard r={kartSatiri} onClose={() => setKartSatiri(null)} />}
      </div>
    </main>
  )
}

/** Aralık özeti kartları — seçili aralıktaki TÜM günleri kapsar. */
function Kartlar({ ozet, aylik }: { ozet: PnlTotals; aylik: boolean }) {
  return (
    <div className="pnl-cards">
      <div className="pnl-card">
        <span className="pnl-card-label" title="Eşleşen (gidiş-dönüş) hacim — 1 MWh alış + 1 MWh satış = 1 MWh">
          Toplam İşlem Hacmi
        </span>
        <span className="pnl-card-value">
          {fmtMwh(ozet.matchedMwh)} <small>MWh</small>
        </span>
        {aylik && (
          <span className="pnl-card-alt" title="Ay içinde dolumu olan saat sayısı">
            {ozet.activeHours} işlemli saat
          </span>
        )}
      </div>
      <div className="pnl-card">
        <span className="pnl-card-label">PnL</span>
        <span className={`pnl-card-value${ton(ozet.pnlTl)}`}>
          {fmtTl(ozet.pnlTl)} <small>TL</small>
        </span>
        {aylik && (
          <span className="pnl-card-alt" title="Ortalama PTF (eşleşen hacimle ağırlıklı)">
            PTF ort. {fmtPrice(ozet.ptfAvg)}
          </span>
        )}
      </div>
      <div className="pnl-card">
        <span className="pnl-card-label">PnL</span>
        <span className={`pnl-card-value${ton(ozet.pnlPerMwh)}`}>
          {fmtTl(ozet.pnlPerMwh)} <small>TL/MWh</small>
        </span>
        {aylik && (
          <span className="pnl-card-alt" title="Dengesizlik ödeşmesinin vade/faiz etkisi — PnL'e dahil">
            fin. etki {fmtTl(ozet.financialImpact)} TL
          </span>
        )}
      </div>
      <div className="pnl-card">
        <span className="pnl-card-label" title="PnL (TL/MWh) ÷ hacim ağırlıklı PTF">
          PnL
        </span>
        <span className={`pnl-card-value${ton(ozet.pnlPctPtf)}`}>
          {fmtPct(ozet.pnlPctPtf)} <small>PTF</small>
        </span>
        {aylik && (
          <span className="pnl-card-alt" title="Kapanmamış kontratların net pozisyonu — PnL'e girmez">
            açık poz. {fmtMwh(ozet.openPosition)} MWh
          </span>
        )}
      </div>
    </div>
  )
}

/* ── Aylık görünüm: bir gün = bir satır ───────────────────────────────────── */

function AylikTable({
  gunler,
  total,
  onGun,
  vurgu,
  onVurgu,
}: {
  gunler: { g: PnlDay; i: number }[]
  total: PnlTotals
  onGun: (idx: number) => void
  vurgu: number | null
  onVurgu: (i: number | null) => void
}) {
  // Aylık tabloda kırılım YOK (kullanıcı isteği 2026-08-02): gün, fiyat,
  // hacim ve üç PnL ölçüsü. Kırılım bir tık ötede — satıra tıklayınca o günün
  // saatlik tablosu açılıyor.
  return (
    <table
      className="ledger-table pnl-table pnl-ay-table"
      onMouseLeave={() => onVurgu(null)}
    >
      <thead>
        <tr>
          <th className="pnl-gun-cell">Tarih</th>
          <th className="num" title="Eşleşen hacimle ağırlıklı ortalama PTF">Ort. PTF</th>
          <th className="num" title="Eşleşen (gidiş-dönüş) hacim — 1 MWh alış + 1 MWh satış = 1 MWh">
            İşlem Hacmi
          </th>
          <th className="num">PnL (TL)</th>
          <th className="num">PnL (TL/MWh)</th>
          <th className="num">PnL (%PTF)</th>
        </tr>
      </thead>
      <tbody>
        {gunler.map(({ g, i }) => (
          <GunSatiri
            key={g.date}
            g={g}
            secili={vurgu === i}
            onGun={() => onGun(i)}
            onVurgu={() => onVurgu(i)}
          />
        ))}
      </tbody>
      <tfoot>
        <tr className="pnl-total-row">
          <td>Ay Toplamı</td>
          <td className="num">{fmtPrice(total.ptfAvg)}</td>
          <td className="num">{hacim(total.matchedMwh)}</td>
          <td className={`num pnl-main${ton(total.pnlTl)}`}>{fmtTl(total.pnlTl)}</td>
          <td className={`num${ton(total.pnlPerMwh)}`}>{fmtTl(total.pnlPerMwh)}</td>
          <td className={`num${ton(total.pnlPctPtf)}`}>{fmtPct(total.pnlPctPtf)}</td>
        </tr>
      </tfoot>
    </table>
  )
}

function GunSatiri({
  g,
  secili,
  onGun,
  onVurgu,
}: {
  g: PnlDay
  secili: boolean
  onGun: () => void
  onVurgu: () => void
}) {
  const t = g.total
  const dolu = doluGun(g)
  // Dolumu olmayan günde toplamlar 0 çıkar; "0,00 TL kâr" ile "işlem yok"
  // aynı şey değil — boş günde PnL sütunları çizgi kalır.
  const pnl = dolu ? t.pnlTl : null
  const sinif = [
    'pnl-ay-satir',
    dolu ? '' : 'pnl-bos',
    haftaSonu(g.date) ? 'pnl-hafta-sonu' : '',
    secili ? 'vurgulu' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <tr
      className={sinif}
      role="button"
      tabIndex={0}
      title={`${tarih(g.date)} — saatlik kırılım için tıkla`}
      onClick={onGun}
      onMouseEnter={onVurgu}
      onFocus={onVurgu}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onGun()
        }
      }}
    >
      <td className="pnl-gun-cell">
        <span className="pnl-gun-tarih">{tarih(g.date)}</span>
        <span className="pnl-gun-ad">{haftaGunu(g.date)}</span>
      </td>
      <td className="num">{fmtPrice(t.ptfAvg)}</td>
      <td className="num">{dolu ? hacim(t.matchedMwh) : DASH}</td>
      <td className={`num pnl-main${ton(pnl)}`}>{fmtTl(pnl)}</td>
      <td className={`num${ton(dolu ? t.pnlPerMwh : null)}`}>{fmtTl(dolu ? t.pnlPerMwh : null)}</td>
      <td className={`num${ton(dolu ? t.pnlPctPtf : null)}`}>{fmtPct(dolu ? t.pnlPctPtf : null)}</td>
    </tr>
  )
}

/* ── Günlük görünüm: bir saat = bir satır ─────────────────────────────────── */

function PnlTable({
  rows,
  total,
  onKart,
}: {
  rows: PnlRow[]
  total: PnlTotals
  onKart: (r: PnlRow) => void
}) {
  // Telefonda TABLO YOK. Yirmi sütunu yatay kaydırmak okumayı imkânsız
  // kılıyordu: ekrana beş sütun sığıyor, PnL'i görmek için her satırda sağa
  // kaydırmak gerekiyordu. Yerine açılır liste — üstte karar için gereken üç
  // sayı, kırılımın tamamı bir dokunuş ötede.
  const mobil = useIsMobile()
  if (mobil) return <PnlMobilListe rows={rows} total={total} onKart={onKart} />

  return (
    <table className="ledger-table pnl-table">
      <thead>
        <tr>
          <th rowSpan={2} className="pnl-kart-col" aria-label="PnL kartı" />
          <th rowSpan={2}>Tarih</th>
          <th rowSpan={2}>Saat</th>
          <th rowSpan={2} className="num">PTF</th>
          <th rowSpan={2} className="num" title="Açıklanmamışsa SMF = PTF varsayılır (silik/italik)">
            SMF
          </th>
          <th rowSpan={2} className="num grp" title="Açık kontratın bekleyen pozisyonu — PnL'e girmez">
            Açık Poz.
          </th>
          <th colSpan={2} className="grp">GİP Alış</th>
          <th colSpan={2} className="grp">GİP Satış</th>
          <th colSpan={3} className="grp" title="Kapanan kontratın net pozisyonu dengesizliğe döner">
            Dengesizlik
          </th>
          <th colSpan={2} className="grp" title="Dengesizlikten gelen alış dahil">Toplam Alış</th>
          <th colSpan={2} className="grp" title="Dengesizlikten gelen satış dahil">Toplam Satış</th>
          <th
            rowSpan={2}
            className="num grp"
            title={
              'Dengesizlik ödeşmesinin vade/faiz etkisi — PnL toplamına dahil. ' +
              'Kısa pozisyonda GİP satış parası erken gelir (getiri, %90 avans), ' +
              'uzunda tersi (maliyet, tam tutar).'
            }
          >
            Finansal Etki
          </th>
          <th rowSpan={2} className="num">PnL (TL)</th>
          <th rowSpan={2} className="num">PnL (TL/MWh)</th>
          <th rowSpan={2} className="num">PnL (%PTF)</th>
        </tr>
        <tr>
          <th className="num grp">Hacim</th>
          <th className="num">AOF</th>
          <th className="num grp">Hacim</th>
          <th className="num">AOF</th>
          <th className="num grp">Hacim</th>
          <th className="num" title="Pozitif dengesizlikte hacim bu fiyattan satılmış sayılır">
            Poz. Fiyat
          </th>
          <th className="num" title="Negatif dengesizlikte hacim bu fiyattan geri alınmış sayılır">
            Neg. Fiyat
          </th>
          <th className="num grp">Hacim</th>
          <th className="num">AOF</th>
          <th className="num grp">Hacim</th>
          <th className="num">AOF</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Satir key={r.contract} r={r} onKart={onKart} />
        ))}
      </tbody>
      <tfoot>
        <tr className="pnl-total-row">
          <td colSpan={5}>Gün Toplamı</td>
          <td className={`num grp${ton(total.openPosition)}`}>{fmtMwh(total.openPosition)}</td>
          <td className="num grp">{hacim(total.buy.mwh)}</td>
          <td className="num">{fmtPrice(total.buy.aof)}</td>
          <td className="num grp">{hacim(total.sell.mwh)}</td>
          <td className="num">{fmtPrice(total.sell.aof)}</td>
          <td className={`num grp${ton(total.imbalance.mwh)}`}>{fmtMwh(total.imbalance.mwh)}</td>
          <td className="num" />
          <td className="num" />
          <td className="num grp">{hacim(total.totalBuy.mwh)}</td>
          <td className="num">{fmtPrice(total.totalBuy.aof)}</td>
          <td className="num grp">{hacim(total.totalSell.mwh)}</td>
          <td className="num">{fmtPrice(total.totalSell.aof)}</td>
          <td className={`num grp${ton(total.financialImpact)}`}>{fmtTl(total.financialImpact)}</td>
          <td className={`num${ton(total.pnlTl)}`}>{fmtTl(total.pnlTl)}</td>
          <td className={`num${ton(total.pnlPerMwh)}`}>{fmtTl(total.pnlPerMwh)}</td>
          <td className={`num${ton(total.pnlPctPtf)}`}>{fmtPct(total.pnlPctPtf)}</td>
        </tr>
      </tfoot>
    </table>
  )
}

/** Sıfır hacim çizgi olarak gösterilir — 24 satırın çoğu boş, göz kalabalığı olmasın. */
function hacim(mwh: number | null | undefined): string {
  return mwh == null || mwh === 0 ? DASH : mwhFmt.format(mwh)
}

function Satir({ r, onKart }: { r: PnlRow; onKart: (r: PnlRow) => void }) {
  const aktif = r.buy.mwh > 0 || r.sell.mwh > 0
  return (
    <tr className={aktif ? undefined : 'pnl-bos'}>
      <td className="pnl-kart-col">
        {aktif && (
          <button
            type="button"
            className="pnl-kart-btn"
            title="PnL kartı — görüntüsünü alıp paylaş"
            aria-label={`${r.contract} PnL kartı`}
            onClick={() => onKart(r)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                 strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="3" />
              <path d="M7 14l3-3.5 2.5 2.5L16 9.5l1.5 1.5" />
            </svg>
          </button>
        )}
      </td>
      <td className="muted">{tarih(r.date)}</td>
      <td className="pnl-saat">{String(r.hour).padStart(2, '0')}</td>
      <td className="num">{fmtPrice(r.ptf)}</td>
      <td className={`num${r.smfProvisional && r.smf != null ? ' smf-gecici' : ''}`}>
        {fmtPrice(r.smf)}
      </td>
      <td
        className={`num grp${r.openPosition != null && r.openPosition !== 0 ? (r.openPosition > 0 ? ' yon-al' : ' yon-sat') : ''}`}
        title={r.openPosition != null ? 'Kontrat açık — akıbeti belli olmadığı için PnL dışı' : undefined}
      >
        {fmtMwh(r.openPosition)}
      </td>
      <td className="num grp">{hacim(r.buy.mwh)}</td>
      <td className="num muted">{fmtPrice(r.buy.aof)}</td>
      <td className="num grp">{hacim(r.sell.mwh)}</td>
      <td className="num muted">{fmtPrice(r.sell.aof)}</td>
      <td className={`num grp${ton(r.imbalance.mwh)}`}>{fmtMwh(r.imbalance.mwh)}</td>
      <td className="num muted">{fmtPrice(r.imbalance.posPrice)}</td>
      <td className="num muted">{fmtPrice(r.imbalance.negPrice)}</td>
      <td className="num grp">{hacim(r.totalBuy.mwh)}</td>
      <td className="num muted">{fmtPrice(r.totalBuy.aof)}</td>
      <td className="num grp">{hacim(r.totalSell.mwh)}</td>
      <td className="num muted">{fmtPrice(r.totalSell.aof)}</td>
      <td
        className={`num grp${ton(r.financialImpact)}`}
        title={r.financialImpact != null ? 'PnL toplamına dahil' : undefined}
      >
        {fmtTl(r.financialImpact)}
      </td>
      <td className={`num pnl-main${ton(r.pnlTl)}`}>{fmtTl(r.pnlTl)}</td>
      <td className={`num${ton(r.pnlPerMwh)}`}>{fmtTl(r.pnlPerMwh)}</td>
      <td className={`num${ton(r.pnlPctPtf)}`}>{fmtPct(r.pnlPctPtf)}</td>
    </tr>
  )
}

/**
 * Günlük görünümün TELEFON hâli — tablo değil, açılır liste.
 *
 *   00   PTF 3.000,0        +1.234,00 TL  ▾
 *   02   PTF 1.500,0        −2.100,00 TL  ▾
 *        └ açılınca: SMF, açık pozisyon, GİP bacakları, dengesizlik,
 *          toplamlar, finansal etki, TL/MWh ve %PTF
 *
 * Neden tablo değil: masaüstü tablosu yirmi sütun. 375 pikselde ekrana beşi
 * sığıyordu, yani PnL'i görmek için HER satırda sağa kaydırmak gerekiyordu —
 * kaydırma da satır satır değil tablo genelinde olduğu için göz sürekli
 * kayboluyordu. Üstte karar için gereken üç sayı duruyor (saat, PTF, PnL),
 * kırılımın tamamı bir dokunuş ötede.
 *
 * Aynı yöntem aylık görünüme uygulanmadı: orada altı sütun var ve satırlar
 * arası karşılaştırma (hangi gün iyi gitti) tablonun asıl işi.
 */
function PnlMobilListe({
  rows,
  total,
  onKart,
}: {
  rows: PnlRow[]
  total: PnlTotals
  onKart: (r: PnlRow) => void
}) {
  //: Aynı anda tek satır açık — art arda birkaçı açılınca liste okunmaz oluyor.
  const [acikSaat, setAcikSaat] = useState<string | null>(null)

  return (
    <div className="pnl-mliste">
      {rows.map((r) => {
        const aktif = r.buy.mwh > 0 || r.sell.mwh > 0
        const acik = acikSaat === r.contract
        return (
          <div
            key={r.contract}
            className={`pnl-msatir${aktif ? '' : ' pnl-bos'}${acik ? ' acik' : ''}`}
          >
            <button
              type="button"
              className="pnl-mbas"
              aria-expanded={acik}
              onClick={() => setAcikSaat(acik ? null : r.contract)}
            >
              <span className="pm-saat">{String(r.hour).padStart(2, '0')}</span>
              <span className="pm-ptf">
                <em>PTF</em>
                {fmtPrice(r.ptf)}
              </span>
              <span className={`pm-pnl${ton(r.pnlTl)}`}>
                {fmtTl(r.pnlTl)}
                <em>TL</em>
              </span>
              <span className="pm-ok" aria-hidden="true">
                {acik ? '▴' : '▾'}
              </span>
            </button>

            {acik && (
              <div className="pnl-mdetay">
                {/* SMF açıklanmamışsa sunucu PTF varsayıyor; o hücre masaüstünde
                    olduğu gibi silik+italik. */}
                <Alan
                  ad="SMF"
                  deger={fmtPrice(r.smf)}
                  gecici={r.smfProvisional && r.smf != null}
                />
                <Alan ad="Açık Poz." deger={fmtMwh(r.openPosition)} />
                <Alan ad="GİP Alış" deger={`${hacim(r.buy.mwh)} @ ${fmtPrice(r.buy.aof)}`} />
                <Alan ad="GİP Satış" deger={`${hacim(r.sell.mwh)} @ ${fmtPrice(r.sell.aof)}`} />
                <Alan ad="Dengesizlik" deger={fmtMwh(r.imbalance.mwh)} ton={ton(r.imbalance.mwh)} />
                <Alan
                  ad="Deng. poz./neg."
                  deger={`${fmtPrice(r.imbalance.posPrice)} / ${fmtPrice(r.imbalance.negPrice)}`}
                />
                <Alan
                  ad="Toplam Alış"
                  deger={`${hacim(r.totalBuy.mwh)} @ ${fmtPrice(r.totalBuy.aof)}`}
                />
                <Alan
                  ad="Toplam Satış"
                  deger={`${hacim(r.totalSell.mwh)} @ ${fmtPrice(r.totalSell.aof)}`}
                />
                <Alan
                  ad="Finansal Etki"
                  deger={fmtTl(r.financialImpact)}
                  ton={ton(r.financialImpact)}
                />
                <Alan ad="PnL (TL/MWh)" deger={fmtTl(r.pnlPerMwh)} ton={ton(r.pnlPerMwh)} />
                <Alan ad="PnL (%PTF)" deger={fmtPct(r.pnlPctPtf)} ton={ton(r.pnlPctPtf)} />

                {aktif && (
                  <button type="button" className="pm-kart" onClick={() => onKart(r)}>
                    PnL kartını aç
                  </button>
                )}
              </div>
            )}
          </div>
        )
      })}

      <div className="pnl-mtoplam">
        <span>Gün Toplamı</span>
        <span className={`num${ton(total.pnlTl)}`}>
          {fmtTl(total.pnlTl)} <em>TL</em>
        </span>
      </div>
    </div>
  )
}

/** Açılan kırılımdaki tek satır: solda etiket, sağda değer. */
function Alan({
  ad,
  deger,
  ton: renk = '',
  gecici = false,
}: {
  ad: string
  deger: string
  ton?: string
  gecici?: boolean
}) {
  return (
    <div className="pm-alan">
      <span className="pm-alan-ad">{ad}</span>
      <span className={`pm-alan-deger num${renk}${gecici ? ' smf-gecici' : ''}`}>{deger}</span>
    </div>
  )
}
